'use client'

import { motion } from 'framer-motion'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

import { useDialog } from '@/hooks'
import { useChangeBaseColor } from '@/hooks/useChangeBaseColor'
import { formatEGiftFormData } from '@/libs/omise'
import UtmHandler from '@/libs/UtmHandler'

import { IPromotion } from '../../types/schema'

import '@/styles/components/_template_eGift_Green.scss'

import FloatingIconEffect from '@/components/ui/floatingIconEffect'
import { cookies } from '@/libs/cookie'

import OrderQuantityForm from './order_quantity_form'
import PaymentInfoForm from './payment_info_Form'

//__PROPS INTERFACE
interface EGiftGreenProps {
  promotionData: IPromotion
}

type OrderItem = {
  name: string
  price: number
  quantity: number
  id: string
  maxQuantity: number
  minQuantity: number
  increase_num: number
}

//__MAIN COMPONENT
const Template_EGiftGreen: React.FC<EGiftGreenProps> = ({ promotionData }) => {
  useChangeBaseColor('bg-[#002126]')
  const t = useTranslations()
  const locale = useLocale()
  const dialog = useDialog()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const errorNotified = useRef(false)
  const [showTerms, setShowTerms] = useState(false)
  const isVideo = (url: string) =>
    url && ['.mp4', '.mov', '.avi', '.webm'].some((ext) => url.toLowerCase().includes(ext))
  const isExpired = promotionData.expired ?? false

  //__FALLBACK IMAGE
  const promotionImage = `${process.env.NEXT_PUBLIC_API_GATEWAY}/${promotionData.promotion_img || '/images/placeholder.jpg'}`

  //__ORDER ITEMS STATE
  const [orderItems, setOrderItems] = useState(() => {
    if (!promotionData.products?.length) return []
    const filteredProducts = promotionData.products.filter(
      (product, index, self) => index === self.findIndex((p) => p.pd_id === product.pd_id)
    )
    return filteredProducts.map((product, idx) => {
      const minQuantity = typeof promotionData.mincust === 'number' ? promotionData.mincust : 1
      const productMaxQuantity = parseInt(product.limit_amount_bill) || 0
      const promotionMaxQuantity = promotionData.maxcust || 0

      const maxQuantity =
        productMaxQuantity > 0 && promotionMaxQuantity > 0
          ? Math.min(productMaxQuantity, promotionMaxQuantity)
          : productMaxQuantity || promotionMaxQuantity

      let quantity = minQuantity //typeof product.qty === 'number' ? product.qty : minQuantity
      if (minQuantity == 0 && idx === 0) {
        quantity = typeof product.qty === 'number' ? product.qty : minQuantity
      }

      return {
        name: product.pd_name,
        price: product.price,
        quantity: quantity,
        id: product.pd_id,
        maxQuantity: maxQuantity,
        minQuantity: minQuantity,
        increase_num: product.increase_num || 1
      }
    })
  })

  //__NOTIFY PAYMENT ERROR FROM QUERY PARAMS (?errorCode=... | ?error=...)
  useEffect(() => {
    if (errorNotified.current) return

    const errorCode = searchParams.get('errorCode')
    const error = searchParams.get('error')

    let message = ''

    if (errorCode) {
      const messageKey = `eGift : Purchase.${errorCode}`
      message = t.has(messageKey) ? t(messageKey) : errorCode
    } else if (error) {
      message = error
    }

    if (!message) return

    errorNotified.current = true

    const title = t.has('eGift : Purchase.ONLINEGIFT_error')
      ? t('eGift : Purchase.Notification_Error')
      : 'Error'

    dialog.alert(<p className='text-center'>{message}</p>, { title, className: 'as-error' })

    //__CLEAN ERROR PARAMS OUT OF THE URL SO IT WON'T SHOW AGAIN ON REFRESH
    const params = new URLSearchParams(searchParams.toString())
    params.delete('errorCode')
    params.delete('error')
    const query = params.toString()
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
  }, [searchParams, pathname, dialog, router, t])

  //__HANDLE QUANTITY
  const handleQuantityChange = (updatedItems: OrderItem[]) => {
    setOrderItems(updatedItems)
  }

  //__HANDLE PAYMENT SUBMIT
  const handlePaymentSubmit = async (formData: any) => {
    const totalPrice = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const language = locale
    const cardToken = formData.cardToken
    const csrf = formData.csrf
    const currentSource = cookies.get('source')
    const currentUtmContent = cookies.get('utm_content')

    if ((formData.pMethod !== 'credit_card' || cardToken) && csrf) {
      let formatted = formatEGiftFormData({
        formData: { ...formData, cardToken, pMethod: formData.pMethod },
        orderItems,
        promotionId: promotionData.promotion_id,
        csrf,
        language,
        totalPrice,
        currency: 'THB',
        utmSource: currentSource,
        utmContent: currentUtmContent
      })

      // Remove any existing pMethod/cardToken fields
      formatted = formatted.filter((f) => f.name !== 'pMethod' && f.name !== 'cardToken')
      // Always add pMethod and cardToken at the end
      formatted.push({ name: 'pMethod', value: formData.pMethod })
      if (formData.cardToken) {
        formatted.push({ name: 'cardToken', value: formData.cardToken })
      }

      try {
        // Create a hidden form element
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = '/payment/'
        form.style.display = 'none'

        // Add each formatted field as a hidden input
        formatted.forEach((field) => {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = field.name
          input.value = field.value.toString()
          form.appendChild(input)
        })

        //__APPEND FORM TO BODY AND SUBMIT
        document.body.appendChild(form)

        form.submit()

        // Clean up - remove form from DOM
        document.body.removeChild(form)
      } catch (error) {
        console.error('Form submission error:', error)
        alert('Payment submission failed. Please try again.')
      }
    } else {
      console.error('Missing required token(s):', { cardToken, csrf })
      alert('Missing required payment information. Please check your payment details.')
    }
  }

  //__RENDER
  return (
    <div className='ui--egift-green'>
      <UtmHandler />
      <FloatingIconEffect />
      <div className='ui--egift-green-header'>
        {promotionData.word_gotpromotion && <h1 className='ui--egift-green-header-title' dangerouslySetInnerHTML={{ __html: promotionData.word_gotpromotion }}></h1>}
        {!isExpired && (
          <div className='ui--egift-green-buy-now-btn'>
            <button
              onClick={() => {
                const element = document.getElementById('payment-form')
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }}
              className='group'>
              {t('eGift : Home Page.GIFTCERTIFICATE_buy_now').toUpperCase()}
              <svg
                className='h-5 w-5 transition-transform duration-300 group-hover:translate-x-1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 7l5 5m0 0l-5 5m5-5H6'
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      <div className='ui--egift-green-media'>
        {isVideo(promotionData.promotion_img) ? (
          <video
            src={promotionImage}
            className='w-full object-cover'
            autoPlay
            loop
            controls={true}
          />
        ) : (
          <Image
            src={promotionImage}
            alt={promotionData.seo_title || 'Promotion Image'}
            width={1029}
            height={460}
            className='w-full object-cover'
            priority
          />
        )}
      </div>
      <div className='ui--egift-green-title-bar'>
        <h2>{promotionData.promotion_rhead}</h2>
      </div>
      <div className='ui--egift-green-card'>
        <div className='relative z-10'>
          <motion.div
            className='motion-container'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            <div
              className='motion-title'
              dangerouslySetInnerHTML={{ __html: promotionData.promotion_tdetail }}
            />
            <div className='my-6 flex justify-center'>
              <button
                onClick={() => setShowTerms(!showTerms)}
                className='flex items-center gap-2 rounded-full border border-gray-400 bg-gray-900/30 px-5 py-2 text-sm text-gray-100 transition-all hover:bg-gray-800/50'>
                <span>Terms & Conditions</span>
                <svg
                  className={`h-4 w-4 transition-transform ${showTerms ? 'rotate-180' : ''}`}
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
            </div>
            {showTerms && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className='terms'>
                <div
                  className='terms-title'
                  dangerouslySetInnerHTML={{ __html: promotionData.promotion_bdetail }}
                />
              </motion.div>
            )}
            <div id='payment-form'>
              <OrderQuantityForm
                initialItems={orderItems}
                onQuantityChange={handleQuantityChange}
                giftRules={promotionData.gift_rules ?? []}
              />
              {!isExpired && (
                <PaymentInfoForm
                  onSubmit={handlePaymentSubmit}
                  orderItems={orderItems}
                  promotionId={promotionData.promotion_id}
                  allowedPayments={promotionData.allowed_payments ?? null}
                />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Template_EGiftGreen
